import { useCallback, useSyncExternalStore } from 'react';
import { api, errorText } from '../../api';
import { useLive, type LiveEvent, type LiveType } from '../../lib/live';
import { useClientApp } from '../context';
import type { NotificationsResponse } from './types';

// Gemeinsam genutzte Daten (Glocke und Übersicht laden dieselben Adressen): eine Anfrage für alle Teile,
// neu geladen bei passenden Live-Ereignissen. Je Login getrennt, damit nach einem Wechsel nichts Fremdes erscheint.

interface Snapshot<T> {
  data: T | undefined;
  error: string | null;
}

interface Entry {
  url: string;
  state: Snapshot<unknown>;
  inflight: boolean;
  again: boolean;
  subs: Set<() => void>;
  handled: WeakSet<LiveEvent>;
}

const entries = new Map<string, Entry>();
const EMPTY: Snapshot<never> = { data: undefined, error: null };

function entryFor(key: string, url: string): Entry {
  let e = entries.get(key);
  if (!e) {
    e = { url, state: EMPTY, inflight: false, again: false, subs: new Set(), handled: new WeakSet() };
    entries.set(key, e);
  }
  return e;
}

async function load(e: Entry): Promise<void> {
  if (e.inflight) {
    e.again = true;
    return;
  }
  e.inflight = true;
  try {
    e.state = { data: await api.get(e.url), error: null };
  } catch (err) {
    e.state = { data: e.state.data, error: errorText(err) };
  } finally {
    e.inflight = false;
    for (const s of e.subs) s();
  }
  if (e.again) {
    e.again = false;
    await load(e);
  }
}

export function useShared<T>(url: string | null, live: LiveType[]) {
  const { me } = useClientApp();
  const key = url ? `${me.user.id}|${url}` : null;
  const subscribe = useCallback(
    (cb: () => void) => {
      if (!key || !url) return () => {};
      const e = entryFor(key, url);
      e.subs.add(cb);
      // erster Nutzer: (neu) laden; bis dahin bleibt der letzte Stand sichtbar
      if (e.subs.size === 1) void load(e);
      return () => {
        e.subs.delete(cb);
      };
    },
    [key, url],
  );
  const state = useSyncExternalStore(subscribe, () => (key ? (entries.get(key)?.state ?? EMPTY) : EMPTY)) as Snapshot<T>;
  useLive(key ? live : null, (ev) => {
    const e = key ? entries.get(key) : undefined;
    // dasselbe Ereignis nur einmal je Adresse neu laden, auch wenn mehrere Teile zuhören
    if (!e || e.handled.has(ev)) return;
    e.handled.add(ev);
    void load(e);
  });
  const reload = useCallback(async () => {
    const e = key ? entries.get(key) : undefined;
    if (e) await load(e);
  }, [key]);
  return { data: state.data, error: state.error, reload };
}

// Glocke: alle Benachrichtigungen des Logins (alle Firmen)
export function useNotifications() {
  return useShared<NotificationsResponse>('/api/app/notifications', ['notifications', 'belege']);
}
